import * as React from 'react';
import { Image, StyleSheet, Text, View, ViewStyle } from 'react-native';

import { newDesignSystemFontStyles, normalizeHeight } from './Styles';
import { getLocalizedTexts } from '../../../Locales';
import { checkIsOsVersionMajorGreaterOrEqual } from '../../../lib/mobileSafariExtension/mobileSafariExtension';
import { VerticalInstructionsVariantContext } from './VerticalInstructions';
import color from '../../../style/color';
import { font } from '../../../style/text';

export type ItemNumberT = 1 | 2 | 3 | 4;

const addressBarIcon = { uri: '/assets/images/icons/mobile-safari-address-bar-icon.svg' };
const logo = { uri: '/assets/images/logos/lightning-rounded.svg' };
const warningIcon = { uri: '/assets/images/icons/inline-warning.svg' };
const numberIcons = {
    1: { uri: '/assets/images/icons/number-1.svg' },
    2: { uri: '/assets/images/icons/number-2.svg' },
    3: { uri: '/assets/images/icons/number-3.svg' },
    4: { uri: '/assets/images/icons/number-4.svg' },
};
const switchOnIcon = { uri: '/assets/images/icons/switch-on.svg' };
const extensionPuzzlePieceIcon = { uri: '/assets/images/icons/extension-puzzle-piece.svg' };

// In the vertical "version B" screens the step index is plain black text on a white background; everywhere else
// it stays the original white-on-black numbered circle SVG. Scoped via context so the control group is untouched.
function InstructionNumber({ itemNumber }: { itemNumber: ItemNumberT }) {
    const isVertical = React.useContext(VerticalInstructionsVariantContext);
    if (isVertical) return <Text style={styles.numberVertical}>{itemNumber}</Text>;
    return <Image source={numberIcons[itemNumber]} style={styles.imageInstructionIcon} />;
}

function InstructionRow({
    itemNumber,
    detailsStyle,
    children,
}: {
    itemNumber: ItemNumberT;
    detailsStyle?: ViewStyle;
    children: React.ReactNode;
}) {
    const isVertical = React.useContext(VerticalInstructionsVariantContext);
    return (
        <View style={[styles.containerInstruction, isVertical && styles.containerInstructionVertical]}>
            <InstructionNumber itemNumber={itemNumber} />
            <View
                style={[
                    styles.containerInstructionsDetails,
                    isVertical && styles.containerInstructionsDetailsVertical,
                    detailsStyle,
                ]}
            >
                {children}
            </View>
        </View>
    );
}

export function InstructionClickSafariSettings({ itemNumber }: { itemNumber: ItemNumberT }) {
    const isVertical = React.useContext(VerticalInstructionsVariantContext);
    const texts = getLocalizedTexts().mobileSafariExtensionInstallationPagesRevamped.instructionsClickSafariSettings;
    const textStyle = isVertical ? newDesignSystemFontStyles.paragraphMedium : newDesignSystemFontStyles.paragraphSmall;
    return (
        <InstructionRow itemNumber={itemNumber}>
            <Text style={textStyle}>{texts.part1}</Text>
            <AddressBarSymbol />
            <Text style={textStyle}>{texts.part2}</Text>
        </InstructionRow>
    );
}

function AddressBarSymbol() {
    const isVertical = React.useContext(VerticalInstructionsVariantContext);
    if (checkIsOsVersionMajorGreaterOrEqual(18))
        return <Image source={addressBarIcon} style={[styles.imageInline, isVertical && styles.imageInlineVertical]} />;
    return (
        <View style={styles.containerInstructionsDetail}>
            <Text>
                <Text style={styles.textContainerInstructionSmall}>A</Text>
                <Text style={styles.textContainerInstruction}>A</Text>
            </Text>
        </View>
    );
}

export function InstructionManageExtensions({ itemNumber }: { itemNumber: ItemNumberT }) {
    const isVertical = React.useContext(VerticalInstructionsVariantContext);
    const texts =
        getLocalizedTexts().mobileSafariExtensionInstallationPagesRevamped.activateStep.instructionsManageExtensions;
    return (
        <InstructionRow itemNumber={itemNumber}>
            {isVertical ? (
                <Text style={newDesignSystemFontStyles.paragraphMedium}>{`${texts.part1} ${texts.part2}`}</Text>
            ) : (
                <>
                    <Text style={newDesignSystemFontStyles.paragraphSmall}>{texts.part1}</Text>
                    <View style={styles.containerInstructionsDetail}>
                        <Text style={styles.textContainerInstruction}>{texts.part2}</Text>
                    </View>
                    <Image source={extensionPuzzlePieceIcon} style={styles.imageExtensionPuzzlePieceIcon} />
                </>
            )}
        </InstructionRow>
    );
}

export function InstructionActivateJokoExtension({ itemNumber }: { itemNumber: ItemNumberT }) {
    const isVertical = React.useContext(VerticalInstructionsVariantContext);
    const text =
        getLocalizedTexts().mobileSafariExtensionInstallationPagesRevamped.activateStep.instructionsActivateJokoExtension;
    if (isVertical)
        return (
            <InstructionRow itemNumber={itemNumber}>
                <Text style={newDesignSystemFontStyles.paragraphMedium}>{text}</Text>
            </InstructionRow>
        );
    return (
        <InstructionRow itemNumber={itemNumber} detailsStyle={{ gap: 8 } as ViewStyle}>
            <Text style={newDesignSystemFontStyles.paragraphSmall}>{text}</Text>
            <View style={styles.containerInstructionIcons}>
                <Image source={logo} style={styles.imageInline} />
                <Image source={switchOnIcon} style={styles.imageSwitchOnIcon} />
            </View>
        </InstructionRow>
    );
}

export function InstructionClickBanner({ itemNumber }: { itemNumber: ItemNumberT }) {
    const texts = getLocalizedTexts().mobileSafariExtensionInstallationPagesRevamped.authorizeStep.instructionsClickBanner;
    return (
        <InstructionRow itemNumber={itemNumber}>
            <Text>
                <Text style={newDesignSystemFontStyles.paragraphSmall}>{texts.part1}</Text>
                <Text style={newDesignSystemFontStyles.paragraphSmallBold}>{texts.part2}</Text>
            </Text>
            <Text style={newDesignSystemFontStyles.paragraphSmall}>{texts.part3}</Text>
        </InstructionRow>
    );
}

export function InstructionClickExtensionWarningIcon({ itemNumber }: { itemNumber: ItemNumberT }) {
    const isVertical = React.useContext(VerticalInstructionsVariantContext);
    const texts =
        getLocalizedTexts().mobileSafariExtensionInstallationPagesRevamped.authorizeStep
            .instructionsClickExtensionWarningIcon;
    const textStyle = isVertical ? newDesignSystemFontStyles.paragraphMedium : newDesignSystemFontStyles.paragraphSmall;
    return (
        <InstructionRow itemNumber={itemNumber}>
            <Text style={textStyle}>{texts.part1}</Text>
            <Image source={warningIcon} style={[styles.warningInline, isVertical && styles.warningInlineVertical]} />
            <Text style={textStyle}>{texts.part2}</Text>
        </InstructionRow>
    );
}

export function InstructionAllowAlways({ itemNumber }: { itemNumber: ItemNumberT }) {
    const isVertical = React.useContext(VerticalInstructionsVariantContext);
    const texts = getLocalizedTexts().mobileSafariExtensionInstallationPagesRevamped.authorizeStep.instructionsAlwaysAllow;
    return (
        <InstructionRow itemNumber={itemNumber}>
            {isVertical ? (
                <Text style={newDesignSystemFontStyles.paragraphMedium}>{`${texts.part1} ${texts.part2}`}</Text>
            ) : (
                <>
                    <Text style={newDesignSystemFontStyles.paragraphSmall}>{texts.part1}</Text>
                    <View style={styles.containerInstructionsDetail}>
                        <Text style={styles.textContainerInstruction}>{texts.part2}</Text>
                    </View>
                </>
            )}
        </InstructionRow>
    );
}

export function InstructionAllowOnAllWebsites({ itemNumber }: { itemNumber: ItemNumberT }) {
    const isVertical = React.useContext(VerticalInstructionsVariantContext);
    const texts =
        getLocalizedTexts().mobileSafariExtensionInstallationPagesRevamped.authorizeStep.instructionsAllowOnAllWebsites;
    return (
        <InstructionRow itemNumber={itemNumber}>
            {isVertical ? (
                <Text style={newDesignSystemFontStyles.paragraphMedium}>{`${texts.part1} ${texts.part2}`}</Text>
            ) : (
                <>
                    <Text style={newDesignSystemFontStyles.paragraphSmall}>{texts.part1}</Text>
                    <View style={styles.containerInstructionsDetail}>
                        <Text style={styles.textContainerInstruction}>{texts.part2}</Text>
                    </View>
                </>
            )}
        </InstructionRow>
    );
}

export function InstructionSystemSettingsClickButton({ itemNumber }: { itemNumber: ItemNumberT }) {
    const text =
        getLocalizedTexts().mobileSafariExtensionInstallationPagesRevamped.missingAuthorization
            .instructionsSystemSettingsClickButton;
    return (
        <InstructionRow itemNumber={itemNumber}>
            <Text style={newDesignSystemFontStyles.paragraphSmall}>{text}</Text>
        </InstructionRow>
    );
}

export function InstructionSystemSettingsOtherWebsites({ itemNumber }: { itemNumber: ItemNumberT }) {
    const texts =
        getLocalizedTexts().mobileSafariExtensionInstallationPagesRevamped.missingAuthorization
            .instructionsSystemSettingsOtherWebsites;
    return (
        <InstructionRow itemNumber={itemNumber}>
            <Text style={newDesignSystemFontStyles.paragraphSmall}>{texts.part1}</Text>
            <View style={styles.containerInstructionsDetail}>
                <Text style={styles.textContainerInstruction}>{texts.part2}</Text>
            </View>
        </InstructionRow>
    );
}

export function InstructionSystemSettingsAllow({ itemNumber }: { itemNumber: ItemNumberT }) {
    const texts =
        getLocalizedTexts().mobileSafariExtensionInstallationPagesRevamped.missingAuthorization
            .instructionsSystemSettingsAllow;
    return (
        <InstructionRow itemNumber={itemNumber}>
            <Text style={newDesignSystemFontStyles.paragraphSmall}>{texts.part1}</Text>
            <View style={styles.containerInstructionsDetail}>
                <Text style={styles.textContainerInstruction}>{texts.part2}</Text>
            </View>
        </InstructionRow>
    );
}

const styles = StyleSheet.create({
    containerInstruction: {
        height: normalizeHeight(25),
        flexDirection: 'row',
        gap: 8,
        alignItems: 'center',
        alignSelf: 'stretch',
    },
    // Vertical "version B" steps can wrap onto two lines, so the fixed single-line height is dropped. The number
    // is vertically centered against the (possibly multi-line) text. The row gap matches the container's
    // horizontal padding (16) and the number is center-aligned in its fixed-width box, so the space on each side
    // of the number (to the border and to the text) is balanced.
    containerInstructionVertical: {
        height: undefined,
        alignItems: 'center',
        gap: 16,
    },
    containerInstructionsDetailsVertical: {
        flexWrap: 'wrap',
    },
    numberVertical: {
        width: normalizeHeight(22),
        fontFamily: font.ambitBold,
        fontSize: 20,
        lineHeight: 20 * 1.2,
        textAlign: 'center',
        color: color.black,
    },
    imageInlineVertical: {
        width: normalizeHeight(13),
        height: normalizeHeight(13),
    },
    warningInlineVertical: {
        width: normalizeHeight(15),
        height: normalizeHeight(15),
    },
    containerInstructionsDetails: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
        flexGrow: 1,
        flexShrink: 0,
        flexBasis: 0,
    },
    containerInstructionsDetail: {
        flexDirection: 'row',
        paddingHorizontal: 8,
        paddingVertical: 3,
        alignItems: 'center',
        borderRadius: 8,
        borderWidth: 1,
        borderColor: 'rgba(0, 0, 0, 0.8)',
    },
    containerInstructionIcons: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: 4,
    },
    textContainerInstruction: {
        fontWeight: 'bold',
        fontSize: 12,
        lineHeight: 14 * 1.4,
        color: 'rgba(0, 0, 0, 0.8)',
    },
    textContainerInstructionSmall: {
        fontWeight: 'bold',
        fontSize: 10,
        lineHeight: 14 * 1.4,
        color: 'rgba(0, 0, 0, 0.8)',
    },
    imageInstructionIcon: {
        width: normalizeHeight(20),
        height: normalizeHeight(20),
    },
    imageInline: {
        width: normalizeHeight(16),
        height: normalizeHeight(16),
    },
    warningInline: {
        width: normalizeHeight(20),
        height: normalizeHeight(20),
    },
    imageSwitchOnIcon: {
        height: normalizeHeight(16),
        width: normalizeHeight(26.323),
        resizeMode: 'stretch',
    },
    imageExtensionPuzzlePieceIcon: {
        height: normalizeHeight(20),
        width: normalizeHeight(20),
    },
});
